import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Mail, Heart, Users, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const metrics = [
  { value: '37.19%', label: 'Email Open Rate', benchmark: 'Industry avg: 25-30%' },
  { value: '180', label: 'Webinar Registrations', benchmark: 'Target achieved' },
  { value: '3,176', label: 'Emails Sent', benchmark: 'Successfully delivered' },
  { value: '37.96%', label: 'Best Performing Version', benchmark: 'A/B Winner' },
];

const objectives = [
  'Engage patients and providers with a cheerful, community-centered tone',
  'Promote podcast recruitment for Season 3',
  'Drive registrations for monthly webinars',
  'Increase traffic to the SUD Resource Hub',
  'Strengthen brand connection and community engagement',
  'Highlight commitment to families impacted by SUD',
];

const strategies = [
  {
    title: 'Brand Recognition',
    icon: Heart,
    description: 'Used branded banner with signature pink zebra pattern for immediate sender identification and trust.',
  },
  {
    title: 'Emotional Engagement',
    icon: Users,
    description: 'Included humor (joke of the month) and warm messaging to create friendly, human connection with readers.',
  },
  {
    title: 'Event Marketing',
    icon: Mail,
    description: 'Bold headlines, branded graphics, speaker photos, and clear CTAs to drive webinar registrations.',
  },
];

const emailSections = [
  {
    title: 'Event Marketing',
    goal: 'Increase webinar attendance',
    tactics: [
      'Bold headline to capture attention',
      'Branded event graphic for recognition',
      'Bullet-style learning objectives for easy scanning',
      'Speaker photos for warmth and credibility',
      'Clear CTA button for immediate registration',
    ],
  },
  {
    title: 'Blog Highlight',
    goal: 'Drive engagement with content',
    tactics: [
      'Narrative-style text that reads like a story',
      'Named partners for credibility',
      'Leadership highlight to personalize',
      'Story progression showing impact',
      'End CTA for deeper engagement',
    ],
  },
  {
    title: 'Podcast Promotion',
    goal: 'Announce new season and drive listens',
    tactics: [
      'Large episode graphic with play-button cue',
      'Guest photo for human connection',
      'Highlighted pull quote as emotional hook',
      'Short narrative summary for context',
      'Blend of storytelling and visual cues',
    ],
  },
  {
    title: 'Resource Hub',
    goal: 'Increase traffic to SUD resources',
    tactics: [
      'Illustrated graphic for attention',
      'Brief text on lived-experience roots',
      'Warm, supportive tone',
      'Clear CTA to explore hub',
    ],
  },
];

export const CaseStudyMoMMAs = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="case-study-mommas">
      {/* Hero Section */}
      <section className="section-padding bg-forest" data-testid="mommas-hero">
        <div className="container-custom">
          <Link 
            to="/work" 
            className="inline-flex items-center gap-2 text-cream/70 hover:text-gold transition-colors mb-8"
            data-testid="back-to-work"
          >
            <ArrowLeft size={20} />
            Back to Work
          </Link>
          
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-6 block">
              Nonprofit / Healthcare
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-cream leading-[1.1] mb-6">
              MoMMAs{' '}
              <span className="text-gold">Voices</span>
            </h1>
            <p className="text-xl text-cream/80 max-w-2xl leading-relaxed">
              Email marketing campaign for a maternal mortality advocacy organization that 
              achieved exceptional engagement rates and drove meaningful community action.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="relative h-[50vh] min-h-[400px]">
        <img 
          src="https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2153811776/settings_images/0f820ee-6e52-58bd-066b-5c6148844c4_MV_Website_Graphic_3_.png"
          alt="MoMMAs Voices Case Study"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/50 to-transparent" />
      </section>

      {/* Key Metrics */}
      <section className="section-padding bg-cream" data-testid="mommas-metrics">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
              Results
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-forest">
              Campaign Performance
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {metrics.map((metric, index) => (
              <motion.div
                key={metric.label}
                className="bg-cream-paper border border-forest/10 rounded-2xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`metric-${index}`}
              >
                <p className="text-3xl md:text-4xl font-bold text-gold mb-2">{metric.value}</p>
                <p className="text-forest font-medium mb-1">{metric.label}</p>
                <p className="text-sm text-forest/60">{metric.benchmark}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About the Client */}
      <section className="section-padding bg-forest-dark" data-testid="mommas-about">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
                The Client
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-cream mb-6">
                About MoMMAs Voices
              </h2>
              <p className="text-cream/70 leading-relaxed mb-6">
                MoMMAs Voices is a maternal mortality and morbidity advocacy organization 
                dedicated to improving maternal health outcomes. They work with patients, 
                providers, and policymakers to create lasting change.
              </p>
              <p className="text-cream/70 leading-relaxed mb-6">
                The organization runs educational webinars, a podcast ("Power of MoMMAs Voices"), 
                and maintains resources for families impacted by substance use disorder.
              </p>
              <div className="flex flex-wrap gap-3">
                {['TexasAIM', 'Texas DSHS', 'TCHMB', 'National Accreta Foundation'].map((partner) => (
                  <span key={partner} className="bg-cream/10 text-cream px-4 py-2 rounded-full text-sm">
                    {partner}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-forest rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-cream mb-6">Campaign Objectives</h3>
                <ul className="space-y-4">
                  {objectives.map((objective, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
                      <span className="text-cream/80">{objective}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Strategy Overview */}
      <section className="section-padding bg-cream" data-testid="mommas-strategy">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
              Our Approach
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-forest">
              Email Marketing Strategy
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {strategies.map((strategy, index) => (
              <motion.div
                key={strategy.title}
                className="bg-cream-paper border border-forest/10 rounded-2xl p-8 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`strategy-overview-${index}`}
              >
                <div className="w-14 h-14 bg-gold rounded-xl flex items-center justify-center mx-auto mb-6">
                  <strategy.icon className="text-forest-dark" size={28} />
                </div>
                <h3 className="text-xl font-semibold text-forest mb-3">{strategy.title}</h3>
                <p className="text-forest/70">{strategy.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Email Sections Breakdown */}
      <section className="section-padding bg-forest" data-testid="mommas-sections">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
              Email Structure
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-cream">
              Section-by-Section Breakdown
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {emailSections.map((section, index) => (
              <motion.div
                key={section.title}
                className="bg-forest-dark/50 backdrop-blur rounded-2xl p-8 border border-cream/10"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`email-section-${index}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <span className="w-8 h-8 bg-gold rounded-full flex items-center justify-center text-forest-dark font-bold text-sm">
                    {index + 1}
                  </span>
                  <h3 className="text-xl font-semibold text-cream">{section.title}</h3>
                </div>
                <p className="text-gold text-sm mb-4">Goal: {section.goal}</p>
                <ul className="space-y-2">
                  {section.tactics.map((tactic, i) => (
                    <li key={i} className="flex items-start gap-2 text-cream/70 text-sm">
                      <span className="text-gold mt-1">•</span>
                      {tactic}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Summary */}
      <section className="section-padding bg-cream-paper" data-testid="mommas-results">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
                The Impact
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-forest mb-8">
                Exceptional Engagement
              </h2>
              <p className="text-lg text-forest/70 leading-relaxed mb-8">
                The campaign achieved a 37.19% open rate—significantly exceeding the industry 
                average of 25-30% for nonprofit and public health emails. A/B testing revealed 
                the winning version at 37.96%, helping optimize future campaigns.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gold/20 rounded-2xl p-6">
                  <p className="text-4xl font-bold text-gold mb-2">37%</p>
                  <p className="text-forest">Open Rate Achieved</p>
                </div>
                <div className="bg-forest/10 rounded-2xl p-6">
                  <p className="text-4xl font-bold text-forest mb-2">180</p>
                  <p className="text-forest/70">Webinar Registrations</p>
                </div>
              </div>
              <p className="text-forest/60 italic">
                "The cheerful, community-centered tone and strategic structure drove 
                meaningful engagement and action from the MoMMAs Voices community."
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gold" data-testid="mommas-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-forest-dark mb-6">
              Need Effective Email Marketing?
            </h2>
            <p className="text-xl text-forest-dark/70 mb-10 max-w-2xl mx-auto">
              Let's create campaigns that engage your audience and drive meaningful results.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="mommas-cta-contact">
                <Button className="bg-forest text-cream hover:bg-forest-dark rounded-full px-10 py-6 text-lg font-semibold">
                  Start Your Project
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/work/afil" data-testid="mommas-cta-prev">
                <Button variant="outline" className="border-2 border-forest-dark text-forest-dark hover:bg-forest-dark hover:text-cream rounded-full px-10 py-6 text-lg">
                  View AFIL Case Study
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyMoMMAs;
